/**
 * +field: the WoW-style XP bar's pure model (hud/field/xpBar.ts draws it): fill fraction, the
 * lit segment count, the labels, and the tween from one state to the next (a level-up rolls the
 * bar over: fill to the end, flash, empty, fill to the new value).
 */
import { groupNumber } from '../hud/kit/statBar';

/** Segments drawn on the bar (WoW: 20 "bubbles"). */
export const XP_SEGMENTS = 20;

export interface XpState {
  level: number;
  /** XP into the level. */
  xp: number;
  /** XP needed for the next level (0 at the cap). */
  next: number;
  maxed: boolean;
}

export interface XpBarView {
  /** 0..1 fill. */
  frac: number;
  /** Segments fully lit (0..XP_SEGMENTS). */
  lit: number;
  /** "Lv 3". */
  levelText: string;
  /** "1,234 / 2,140" (or "Max level"). */
  valueText: string;
  /** "57%" (empty at the cap). */
  pctText: string;
  /** Hover line: "Level 3 · 1,234 / 2,140 XP (57%) · 906 to level 4". */
  title: string;
}

export function xpFrac(s: XpState): number {
  if (s.maxed) return 1;
  if (!(s.next > 0)) return 0;
  return Math.min(1, Math.max(0, s.xp / s.next));
}

export function xpBarView(s: XpState): XpBarView {
  const frac = xpFrac(s);
  const lit = Math.min(XP_SEGMENTS, Math.floor(frac * XP_SEGMENTS + 1e-9));
  const levelText = `Lv ${s.level}`;
  if (s.maxed) return { frac, lit: XP_SEGMENTS, levelText, valueText: 'Max level', pctText: '', title: `Level ${s.level} · max level for now` };
  const pct = Math.floor(frac * 100);
  const valueText = `${groupNumber(Math.max(0, Math.floor(s.xp)))} / ${groupNumber(s.next)}`;
  const left = Math.max(0, s.next - Math.floor(s.xp));
  return { frac, lit, levelText, valueText, pctText: `${pct}%`, title: `Level ${s.level} · ${valueText} XP (${pct}%) · ${groupNumber(left)} to level ${s.level + 1}` };
}

/** How the bar gets from `prev` to `cur`. */
export interface XpTween {
  /** Level-ups on the way (each one fills to the end, flashes and empties). */
  rollovers: number;
  /** Fill to start from, and to end on. */
  from: number;
  to: number;
}

export function xpTween(prev: XpState | null, cur: XpState): XpTween {
  const to = xpFrac(cur);
  if (!prev) return { rollovers: 0, from: to, to };
  const rollovers = Math.max(0, cur.level - prev.level);
  return { rollovers, from: xpFrac(prev), to };
}
